// LT-360 — Lifecycle history timeline panel for the contact-detail page.
//
// Renders the append-only audit trail of contact.lifecycle_stage
// transitions, newest first. Each entry shows: from-stage → to-stage,
// trigger icon (manual / sequence / api / backfill), timestamp, and
// optional reason text.
//
// Mirrors the deal-stage-history timeline pattern (parent §10.2). The
// backend endpoint is GET /contacts/{id}/lifecycle-history.

import React, { useEffect, useState } from 'react';
import { User, Bot, Workflow, History as HistoryIcon, Clock } from 'lucide-react';

import { contactsAPI } from '../services/contactsAPI';
import {
  ContactLifecycleHistoryEntry,
  LifecycleHistoryTrigger,
  LIFECYCLE_STAGE_LABELS,
  type LifecycleStage,
} from '../types/contact';
import { Card, EmptyState } from '../ui';

const TRIGGER_LABEL: Record<LifecycleHistoryTrigger, string> = {
  manual: 'Operator',
  sequence: 'Sequence step',
  api: 'Automated',
  backfill: 'Backfill',
};

const TRIGGER_ICON: Record<
  LifecycleHistoryTrigger,
  React.ComponentType<{ className?: string }>
> = {
  manual: User,
  sequence: Workflow,
  api: Bot,
  backfill: Clock,
};

function stageLabel(stage: string | null): string {
  if (!stage) return '—';
  if (stage in LIFECYCLE_STAGE_LABELS) {
    return LIFECYCLE_STAGE_LABELS[stage as LifecycleStage];
  }
  return stage;
}

export interface LifecycleHistoryTimelineProps {
  contactId: string;
  /** Override default 50-entry fetch limit. */
  limit?: number;
}

const LifecycleHistoryTimeline: React.FC<LifecycleHistoryTimelineProps> = ({
  contactId,
  limit = 50,
}) => {
  const [entries, setEntries] = useState<ContactLifecycleHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const rows = await contactsAPI.getLifecycleHistory(contactId, { limit });
        if (!cancelled) setEntries(rows);
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : 'Failed to load lifecycle history');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactId, limit]);

  if (loading) {
    return (
      <Card>
        <p className="text-slate-400 text-sm">Loading lifecycle history…</p>
      </Card>
    );
  }

  if (err) {
    return (
      <Card>
        <p className="text-danger-text text-sm">{err}</p>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <EmptyState
        surface="lifecycle_history"
        icon={HistoryIcon}
        headline="No lifecycle changes yet"
        subhead="Every transition for this contact will appear here in chronological order."
        primary={{ label: '', onClick: () => undefined }}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <HistoryIcon className="w-4 h-4 text-slate-400" strokeWidth={2} />
          <h3 className="text-sm font-semibold text-slate-200">Lifecycle history</h3>
          <span className="font-mono text-xs text-slate-400">{entries.length}</span>
        </div>
        <ol className="space-y-3">
          {entries.map((e) => {
            const Icon = TRIGGER_ICON[e.trigger];
            const triggerTitle = TRIGGER_LABEL[e.trigger];
            return (
              <li
                key={e.id}
                className="flex items-start gap-3 border-l-2 border-white/[0.08] pl-3 py-1"
              >
                <div
                  className="p-1.5 rounded-ds-md bg-info-tint border border-info-border"
                  title={triggerTitle}
                >
                  <Icon className="w-3.5 h-3.5 text-info" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-200">
                    {e.from_stage ? (
                      <>
                        <span className="text-slate-400">{stageLabel(e.from_stage)}</span>
                        <span className="mx-1.5 text-slate-500">→</span>
                        <span className="font-medium">{stageLabel(e.to_stage)}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-slate-500">Initial:</span>{' '}
                        <span className="font-medium">{stageLabel(e.to_stage)}</span>
                      </>
                    )}
                  </p>
                  <p className="text-xs text-slate-400 font-mono">
                    {new Date(e.transitioned_at).toLocaleString()} · {triggerTitle}
                  </p>
                  {e.reason && (
                    <p className="text-xs text-slate-300 mt-1 italic">{e.reason}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </Card>
  );
};

export default LifecycleHistoryTimeline;
